import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Typography } from '@mui/material';
import { TextSnippet } from '@mui/icons-material';
import { Checkbox } from 'primereact/checkbox';
import { Paginator } from 'primereact/paginator';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import { useGetUsersAuth0Mutation } from '../../services/users/usersService';
import { EditUserAndRoleSideBar } from './editUserAndRoleSideBar/editUserAndRoleSideBar';
import { AddNewSystemUser } from './addSystemUser/addSystemUser';
import { LoadingRowsComponent } from '../../components/commons/LoadingRows/LoadingRows';
import { RightSideBar } from '../../components/layout/rightSideBar/rightSideBar';
import { UserSideBar } from './userSidebar/userSidebar';
import { AddLocationToUser } from './addLocationToUser/addLocationToUser';
import { ManageRolesUser } from './manageRolesUser/manageRolesUser';
import { CreateRoleUser } from './createRoleUser/createRoleUser';
import { AddAdmin } from './addAdmin/addAdmin';
import { UsersTemplateProps } from './usersTemplateProps';
import { StatusUser, StyledButtonName } from './userTemplateStyles';
import { viewRightSideBarEnum } from '../../constants/sidebarEnums';
import Strings from '../../i18n';
import { IUserInfo } from '../../types/Auth/authInterface';
import {
  StyledBoxTable,
  styledCheckboxHeader,
  styledColumnHeaderCheckbox
} from '../../components/layout/main/mainStyles';
import { MultipleUsersSelectedMethods } from './multipleUsersSelectedMethods/multipleUsersSelectedMethods';
import { AvatarUserComponent } from '../../components/commons/AvatarUser/avatarUser';
import { AddNoteSideBar } from '../../components/commons/notes/addNoteSideBar/addNoteSideBar';
import { UsersFilters } from './usersFilters/usersFiltersSideBar';
import { ChangeUserPassword } from './changeUserPassword/changeUserPassword';
import { ButtonIconStyled } from '../locations/allLocationsList/allLocationListStyles';

export const UsersTemplate = ({
  sideBarOpenFor,
  openRightSideBarForMethod,
  openedSideBar,
  setOpenSideBar,
  selectedUsers,
  setSelectedUsers
}: UsersTemplateProps) => {
  const [paginator, setPaginator] = useState({
    currentPage: 1,
    rows: 50
  });
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(search);
  const searchUser = queryParams.get('searchUser');
  const filterByLabel = queryParams.get('filterByLabel');
  const filterByRole = queryParams.get('filterByRole');
  const [allUsers, setAllUsers] = useState<IUserInfo[]>([]);
  const [getUsers, { isLoading, isSuccess, data: dataUser }] =
    useGetUsersAuth0Mutation();

  const [selectedUser, setSelectedUser] = useState<IUserInfo | null>(null);

  const loadTable = () => {
    getUsers({
      page: paginator.currentPage,
      pageSize: paginator.rows,
      filterByLabel: filterByLabel ?? '',
      filterByRole: filterByRole ?? ''
    });
    setOpenSideBar(false);
    setSelectedUsers([]);
  };

  useEffect(() => {
    loadTable();
  }, [paginator.currentPage, paginator.rows, filterByLabel, filterByRole]);

  const getInfoStringFromModuleAndPermissions = (
    module: string,
    permissions: string[]
  ) => {
    return `${module}: ${permissions.map((element) => element)}`;
  };

  const switchComponentToShowRightSideBar = () => {
    switch (sideBarOpenFor) {
      case viewRightSideBarEnum.editUserPermissions:
        return (
          <ManageRolesUser
            openRightSideBarForMethod={openRightSideBarForMethod}
            setOpenSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.manageUserRoles:
        return (
          <ManageRolesUser
            openRightSideBarForMethod={openRightSideBarForMethod}
            setOpenSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.createUserRole:
        return (
          <CreateRoleUser
            openRightSideBarForMethod={openRightSideBarForMethod}
            setOpenSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.addAdmin:
        return <AddAdmin />;
      case viewRightSideBarEnum.editUserAndRole:
        return selectedUser ? (
          <EditUserAndRoleSideBar
            user={selectedUser}
            reloadTable={async () => loadTable()}
            openRightSideBarForMethod={openRightSideBarForMethod}
            setOpenSideBar={setOpenSideBar}
            setUsersSelected={setSelectedUsers}
          />
        ) : (
          <div />
        );
      case viewRightSideBarEnum.editMultipleUsers:
        return (
          <MultipleUsersSelectedMethods
            selectedUsers={selectedUsers}
            reloadTable={loadTable}
            setOpenSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.addUserToDefaultLocation:
        return (
          <AddLocationToUser
            user={selectedUser}
            reloadTable={loadTable}
            setOpenRightSideBar={setOpenSideBar}
            setUsersSelected={setSelectedUsers}
          />
        );
      case viewRightSideBarEnum.addSystemUser:
        return (
          <AddNewSystemUser
            reloadTable={async () => loadTable()}
            openRightSideBarForMethod={openRightSideBarForMethod}
            setOpenSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.addNote:
        return (
          <AddNoteSideBar
            closeSideBar={() => setOpenSideBar(false)}
            targetId={selectedUser?.userId ?? 0}
            type="user"
          />
        );
      case viewRightSideBarEnum.changeUserPassword:
        return (
          <ChangeUserPassword
            selectedUser={selectedUser ?? ({} as IUserInfo)}
            setOpenSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.filters:
        return (
          <UsersFilters
            reloadTable={async () => loadTable()}
            setOpenRightSideBar={setOpenSideBar}
            openRightSideBar={openedSideBar}
          />
        );
      default:
        return <UserSideBar setOpenSideBar={setOpenSideBar} />;
    }
  };
  const redirectToNotes = (item: IUserInfo) => {
    navigate('/my-notes', {
      state: {
        type: 'Users',
        id: item.userId
      }
    });
  };
  const getFullName = (item: IUserInfo) => {
    return (
      <>
        <StyledButtonName needsInformation={item.firstName === ''}>
          <AvatarUserComponent
            key={item.userId}
            fullName={`${item.firstName} ${item.lastName}`}
            size={25}
          />
          {item.firstName !== ''
            ? `${item.firstName} ${item.lastName}`
            : Strings.common.missingInformation}
        </StyledButtonName>
        {item.hasNotes && (
          <ButtonIconStyled size="small" onClick={() => redirectToNotes(item)}>
            <TextSnippet fontSize="small" />
          </ButtonIconStyled>
        )}
      </>
    );
  };

  const getDate = (item: IUserInfo) => {
    const utcDate = moment.utc(item.lastLogin, 'DD MMM YYYY HH:mm');
    const browserTime = utcDate.local();
    return item.lastLogin ? (
      <Typography>{browserTime.format('YYYY/MM/DD, h:mmA')}</Typography>
    ) : (
      ''
    );
  };

  const getDefaultLoc = (item: IUserInfo) => {
    return (
      <Link to={`/locations/${item.defaultLocationId}/devices`}>
        {item.defaultLocationName}
      </Link>
    );
  };

  const getHeaderCheckbox = () => {
    return (
      <Checkbox
        className="p-checkbox"
        style={{
          visibility: selectedUsers.length ? 'visible' : 'hidden',
          ...styledCheckboxHeader
        }}
        checked={(selectedUsers && selectedUsers.length > 0) ?? false}
        onChange={() => {
          setSelectedUsers([]);
        }}
      />
    );
  };
  useEffect(() => {
    if (selectedUsers.length === 1) {
      openRightSideBarForMethod(viewRightSideBarEnum.editUserAndRole);
      setSelectedUser(selectedUsers[0]);
    } else if (selectedUsers.length > 1) {
      openRightSideBarForMethod(viewRightSideBarEnum.editMultipleUsers);
    } else {
      openRightSideBarForMethod(viewRightSideBarEnum.empty);
      setOpenSideBar(false);
    }
  }, [selectedUsers]);

  const reorderUsers = (usersList: IUserInfo[], userIdToMove: number) => {
    const usersArrayCopy = [...usersList];
    return usersArrayCopy.sort((a, b) => {
      if (a.userId === userIdToMove) return -1;
      if (b.userId === userIdToMove) return 1;
      return 0;
    });
  };
  useEffect(() => {
    if (isSuccess && dataUser) {
      setPaginator({
        currentPage: dataUser.currentPage,
        rows: dataUser.pageSize
      });
      let filteredUsers = dataUser.data;

      if (searchUser) {
        setSelectedUsers(
          dataUser.data.filter((user) => user.userId === +searchUser)
        );
        filteredUsers = reorderUsers(dataUser.data, +searchUser);
      }

      setAllUsers(filteredUsers);
      if (filterByLabel) {
        if (dataUser.data.length === 1) {
          setSelectedUsers([dataUser.data[0]]);
        }
      }
    }
  }, [isSuccess, searchUser]);

  const onPageChange = (event: any) => {
    setPaginator({
      currentPage: event.page + 1,
      rows: event.rows
    });
    loadTable();
  };
  const footer = (
    <div className="row">
      <div className="col-12">
        <Paginator
          first={(paginator.currentPage - 1) * paginator.rows}
          rows={paginator.rows}
          totalRecords={dataUser?.totalRecords ?? 300}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
  return (
    <>
      <StyledBoxTable>
        {isLoading && <LoadingRowsComponent />}
        {isSuccess && allUsers && (
          <DataTable
            selectionMode="multiple"
            selection={selectedUsers}
            value={allUsers}
            size="small"
            scrollable
            scrollHeight="flex"
            rows={paginator.rows}
            onSelectionChange={(e) => setSelectedUsers(e.value)}
            tableStyle={{ padding: 0, margin: 0, fontSize: '14px' }}
            footer={footer}
            metaKeySelection
          >
            <Column
              selectionMode="multiple"
              headerStyle={styledColumnHeaderCheckbox}
              header={getHeaderCheckbox}
              frozen
            />
            <Column
              field="firstName"
              header="Name"
              frozen
              className="font-bold"
              style={{ minWidth: '200px' }}
              body={getFullName}
              sortable
            />
            <Column field="email" header="Email" sortable />
            <Column
              field="totalRequests"
              header="Total Request"
              sortable
              style={{ width: '150px', textAlign: 'center' }}
            />
            <Column
              field="accountDateCreated"
              header="Creation Date"
              sortable
            />
            <Column
              field="lastLogin"
              header="Last Login"
              sortable
              dataType="Date"
              body={getDate}
            />
            <Column field="userSystemRoleName" header="User Type" sortable />
            <Column
              field="userSystemRoleName"
              header="Default Location"
              sortable
              body={getDefaultLoc}
            />
          </DataTable>
        )}
      </StyledBoxTable>
      <RightSideBar
        open={openedSideBar}
        onMenuClose={() => {
          setOpenSideBar(false);
        }}
      >
        {switchComponentToShowRightSideBar()}
      </RightSideBar>
    </>
  );
};
